import React, { useState, useRef } from 'react'
import * as api from '../../../app/axios/api'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

const StudentRegistration = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const courseOptions = [
    { value: '', label: 'Select Course', fee: 0, duration: 0 },
    {
      value: 'cfm',
      label: 'Computer Fundamental Mastery',
      fee: 6000,
      duration: 3,
    },
    {
      value: 'web_design',
      label: 'Web Design',
      fee: 5000,
      duration: 3,
    },
    {
      value: 'programming',
      label: 'Programming',
      fee: 22000,
      duration: 3,
    },
    { value: 'frontend', label: 'Frontend', fee: 33000, duration: 6 },
    {
      value: 'fullstack',
      label: 'Full Stack',
      fee: 40000,
      duration: 12,
    },
    { value: 'o_level', label: 'O Level', fee: 15000, duration: 12 },
    {
      value: 'cs_1_year_summer',
      label: 'CS 1 Year Summer',
      fee: 4000,
      duration: 1,
    },
    {
      value: 'cs_2_year_summer',
      label: 'CS 2 Year Summer',
      fee: 5500,
      duration: 2,
    },
    {
      value: 'ec_1_year_summer',
      label: 'EC 1 Year Summer',
      fee: 5500,
      duration: 1,
    },
    {
      value: 'ec_2_year_summer',
      label: 'EC 2 Year Summer',
      fee: 6000,
      duration: 2,
    },
  ]

  const getInitialFormData = () => ({
    enrollment_number: '',
    registration_date: new Date().toISOString().split('T')[0],
    session: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
    name: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    father_occupation: '',
    mother_name: '',
    phone_number: '',
    parent_phone_number: '',
    email: '',
    class: '',
    course: '',
    course_duration: '',
    school_or_college_name: '',
    subject_or_stream: '',
    address: '',
    city: '',
    pin_code: '',
    fee: '',
    due_fee: '',
    discount: '',
    status: 'running',
    profile_image: null,
  })

  const [formData, setFormData] = useState(getInitialFormData())
  const [declarationAccepted, setDeclarationAccepted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => {
      let finalValue = value
      if (name === 'course_duration') {
        finalValue = value === '' ? '' : parseInt(value, 10) || 0
      }

      const updatedFormData = { ...prev, [name]: finalValue }

      if (name === 'course') {
        const selectedCourse = courseOptions.find(
          (option) => option.value === value,
        )
        updatedFormData.fee = selectedCourse
          ? selectedCourse.fee.toString()
          : '0'
        updatedFormData.course_duration = selectedCourse
          ? selectedCourse.duration
          : ''
      }

      if (name === 'fee' || name === 'discount' || name === 'course') {
        const feeAmount = parseFloat(updatedFormData.fee) || 0
        const discountAmount = parseFloat(updatedFormData.discount) || 0
        updatedFormData.due_fee = (feeAmount - discountAmount).toString()
      }

      return updatedFormData
    })
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be smaller than 2MB')
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          profile_image: reader.result,
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async () => {
    if (!declarationAccepted) {
      toast.error('Please accept the declaration checkbox.')
      return
    }

    setLoading(true)
    try {
      const submissionData = {
        enrollment_number: formData.enrollment_number,
        registration_date: formData.registration_date,
        session: formData.session,
        name: formData.name,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        father_name: formData.father_name,
        father_occupation: formData.father_occupation,
        mother_name: formData.mother_name,
        phone_number: formData.phone_number,
        parent_phone_number: formData.parent_phone_number,
        email: formData.email,
        class: formData.class,
        course: formData.course,
        course_duration: parseInt(formData.course_duration, 10) || 0,
        school_or_college_name: formData.school_or_college_name,
        subject_or_stream: formData.subject_or_stream,
        address: formData.address,
        city: formData.city,
        pin_code: formData.pin_code,
        fee: parseInt(formData.fee, 10) || 0,
        due_fee: parseInt(formData.due_fee, 10) || 0,
        discount: parseInt(formData.discount, 10) || 0,
        status: formData.status,
        profile_image: formData.profile_image,
      }

      await api.registration(submissionData)
      toast.success('Registration completed successfully!')
      navigate('/')

      setFormData(getInitialFormData())
      setDeclarationAccepted(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong'

      toast.error(errorMessage)
      console.error('Registration error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div
        id='screen-content'
        className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-6 md:p-8'
      >
        <div className='max-w-4xl mx-auto'>
          {/* Theme Header */}
          <div className='text-center mb-6 sm:mb-8'>
            <h1 className='text-3xl sm:text-4xl font-bold text-slate-900 mb-2'>
              Student Registration
            </h1>
            <p className='text-sm sm:text-base text-indigo-600 font-medium'>
              Fill in the details to register a new student
            </p>
          </div>

          {/* Core Form Card */}
          <div className='bg-white rounded-xl sm:rounded-2xl shadow-xl border border-indigo-50 p-4 sm:p-8 space-y-6 sm:space-y-8'>
            {/* Section 1: Official Identifiers */}
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-indigo-950 pb-2 mb-4 border-b border-indigo-100 flex items-center'>
                <span className='bg-indigo-100 text-indigo-700 w-7 h-7 rounded-full inline-flex items-center justify-center text-xs sm:text-sm font-bold mr-3 shrink-0'>
                  1
                </span>
                Official Identifiers
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Enrollment Number *
                  </label>
                  <input
                    type='text'
                    name='enrollment_number'
                    value={formData.enrollment_number}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 outline-none text-base sm:text-sm'
                    placeholder='Enter enrollment number'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Registration Date *
                  </label>
                  <input
                    type='date'
                    name='registration_date'
                    value={formData.registration_date}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 outline-none text-base sm:text-sm'
                  />
                </div>
                <div className='sm:col-span-2 md:col-span-1'>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Academic Session *
                  </label>
                  <input
                    type='text'
                    name='session'
                    value={formData.session}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 outline-none text-base sm:text-sm'
                    placeholder='e.g., 2024-2025'
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Student Personal Profile */}
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-indigo-950 pb-2 mb-4 border-b border-indigo-100 flex items-center'>
                <span className='bg-indigo-100 text-indigo-700 w-7 h-7 rounded-full inline-flex items-center justify-center text-xs sm:text-sm font-bold mr-3 shrink-0'>
                  2
                </span>
                Personal Information
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                <div className='md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                      Student Name *
                    </label>
                    <input
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                      placeholder='As per official documents'
                    />
                  </div>
                  <div>
                    <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                      Date of Birth *
                    </label>
                    <input
                      type='date'
                      name='date_of_birth'
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    />
                  </div>
                  <div className='sm:col-span-2'>
                    <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                      Gender *
                    </label>
                    <select
                      name='gender'
                      value={formData.gender}
                      onChange={handleChange}
                      required
                      className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 bg-white rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    >
                      <option value=''>Select Gender</option>
                      <option value='Male'>Male</option>
                      <option value='Female'>Female</option>
                      <option value='Other'>Other</option>
                    </select>
                  </div>
                </div>

                {/* Profile Photo Upload */}
                <div className='flex flex-col items-center justify-center border-2 border-dashed border-indigo-100 p-4 sm:p-6 rounded-xl bg-indigo-50/50 w-full'>
                  {formData.profile_image ? (
                    <img
                      src={formData.profile_image}
                      alt='Student Preview'
                      className='w-24 h-24 object-cover rounded-lg shadow-md mb-3 border border-indigo-200'
                    />
                  ) : (
                    <div className='w-16 h-16 sm:w-20 sm:h-20 bg-indigo-100 flex items-center justify-center rounded-full text-indigo-500 text-2xl sm:text-3xl mb-3'>
                      🧑‍🎓
                    </div>
                  )}
                  <input
                    type='file'
                    accept='image/*'
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className='hidden'
                    id='profile-image-upload'
                  />
                  <label
                    htmlFor='profile-image-upload'
                    className='cursor-pointer text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-all duration-200 inline-block text-center'
                  >
                    Choose Photo
                  </label>
                </div>
              </div>
            </div>

            {/* Section 3: Family & Contact Relations */}
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-indigo-950 pb-2 mb-4 border-b border-indigo-100 flex items-center'>
                <span className='bg-indigo-100 text-indigo-700 w-7 h-7 rounded-full inline-flex items-center justify-center text-xs sm:text-sm font-bold mr-3 shrink-0'>
                  3
                </span>
                Family & Contact Information
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Father's Name *
                  </label>
                  <input
                    type='text'
                    name='father_name'
                    value={formData.father_name}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder="Enter father's name"
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Father's Occupation *
                  </label>
                  <input
                    type='text'
                    name='father_occupation'
                    value={formData.father_occupation}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='e.g., Business, Service'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Mother's Name (Optional)
                  </label>
                  <input
                    type='text'
                    name='mother_name'
                    value={formData.mother_name}
                    onChange={handleChange}
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder="Enter mother's name"
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Student Phone *
                  </label>
                  <input
                    type='tel'
                    name='phone_number'
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                    maxLength='10'
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='10 digit number'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Parent Phone *
                  </label>
                  <input
                    type='tel'
                    name='parent_phone_number'
                    value={formData.parent_phone_number}
                    onChange={handleChange}
                    required
                    maxLength='10'
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='10 digit number'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Email ID (Optional)
                  </label>
                  <input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='student@domain.com'
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Physical Address */}
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-indigo-950 pb-2 mb-4 border-b border-indigo-100 flex items-center'>
                <span className='bg-indigo-100 text-indigo-700 w-7 h-7 rounded-full inline-flex items-center justify-center text-xs sm:text-sm font-bold mr-3 shrink-0'>
                  4
                </span>
                Residential Address
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6'>
                <div className='sm:col-span-3 lg:col-span-1'>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Correspondence Address *
                  </label>
                  <input
                    type='text'
                    name='address'
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='House, Street, Area'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    City / Town *
                  </label>
                  <input
                    type='text'
                    name='city'
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='City'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Pincode *
                  </label>
                  <input
                    type='text'
                    name='pin_code'
                    value={formData.pin_code}
                    onChange={handleChange}
                    required
                    maxLength='6'
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='6 Digits'
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Academic Preferences */}
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-indigo-950 pb-2 mb-4 border-b border-indigo-100 flex items-center'>
                <span className='bg-indigo-100 text-indigo-700 w-7 h-7 rounded-full inline-flex items-center justify-center text-xs sm:text-sm font-bold mr-3 shrink-0'>
                  5
                </span>
                Academic Information
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Class *
                  </label>
                  <input
                    type='text'
                    name='class'
                    value={formData.class}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='e.g., 10th, 12th, B.Tech'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Course *
                  </label>
                  <select
                    name='course'
                    value={formData.course}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 bg-white rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                  >
                    {courseOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Course Duration *
                  </label>
                  <input
                    type='number'
                    name='course_duration'
                    value={formData.course_duration}
                    onChange={handleChange}
                    required
                    min='0'
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='e.g., 3, 6, 12'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Subject / Stream *
                  </label>
                  <input
                    type='text'
                    name='subject_or_stream'
                    value={formData.subject_or_stream}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='e.g., PCM, Commerce, CS'
                  />
                </div>
                <div className='sm:col-span-2 lg:col-span-1'>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Institution Name *
                  </label>
                  <input
                    type='text'
                    name='school_or_college_name'
                    value={formData.school_or_college_name}
                    onChange={handleChange}
                    required
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-base sm:text-sm outline-none'
                    placeholder='School/College name'
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Fee Structure & Calculations */}
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-indigo-950 pb-2 mb-4 border-b border-indigo-100 flex items-center'>
                <span className='bg-indigo-100 text-indigo-700 w-7 h-7 rounded-full inline-flex items-center justify-center text-xs sm:text-sm font-bold mr-3 shrink-0'>
                  6
                </span>
                Fee Details
              </h2>
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 bg-gradient-to-r from-slate-50 to-indigo-50/30 p-4 sm:p-5 rounded-xl border border-indigo-100'>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Total Fee (₹) *
                  </label>
                  <input
                    type='number'
                    name='fee'
                    value={formData.fee}
                    onChange={handleChange}
                    readOnly
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 bg-slate-100 rounded-lg text-base sm:text-sm font-bold text-slate-800'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Discount (₹)
                  </label>
                  <input
                    type='number'
                    name='discount'
                    value={formData.discount}
                    onChange={handleChange}
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-indigo-500 outline-none'
                    placeholder='0.00'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-slate-700 uppercase mb-2 tracking-wider'>
                    Due Fee Balance (₹)
                  </label>
                  <input
                    type='number'
                    name='due_fee'
                    value={formData.due_fee}
                    onChange={handleChange}
                    readOnly
                    className='w-full px-4 py-2.5 sm:py-3 border border-slate-300 bg-slate-100 rounded-lg text-base sm:text-sm font-bold text-red-600'
                  />
                </div>
              </div>
            </div>

            {/* Undertaking Declaration */}
            <div className='pt-4 flex items-start gap-3 border-t border-indigo-100'>
              <input
                type='checkbox'
                id='declare-check'
                checked={declarationAccepted}
                onChange={(e) => setDeclarationAccepted(e.target.checked)}
                className='mt-1 cursor-pointer h-4 w-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 shrink-0'
              />
              <label
                htmlFor='declare-check'
                className='text-xs text-slate-600 cursor-pointer select-none leading-relaxed'
              >
                I hereby declare that all entries made in this application form
                are true, complete and correct. I understand that if any details
                are found incorrect, my admission is liable to be cancelled
                without prior notice.
              </label>
            </div>

            {/* Form Actions */}
            <div className='pt-4 sm:pt-6 border-t border-slate-200 flex flex-col sm:flex-row gap-4'>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={loading}
                className='w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 sm:py-4 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center shadow-lg text-sm sm:text-base'
              >
                {loading ? (
                  <>
                    <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2'></div>
                    Registering Student...
                  </>
                ) : (
                  <>
                    <span className='mr-2'>💾</span>
                    Register & Save Record
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default StudentRegistration
